import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, Scan, CheckCircle, XCircle, Camera, AlertCircle, 
  LogOut, Store, MapPin, Lock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "scc_scanner_session";

export default function PublicScanner() {
  // Session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Login state
  const [merchants, setMerchants] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [pin, setPin] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  
  // Scanner state
  const [scanResult, setScanResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [scannerError, setScannerError] = useState(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    checkExistingSession();
    fetchMerchants();
    
    return () => {
      stopScanner();
    };
  }, []);

  const checkExistingSession = async () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Verify session is still valid
        const response = await axios.get(`${API}/public/scanner/session`, {
          headers: { Authorization: `Bearer ${parsed.token}` }
        });
        setSession({ ...parsed, ...response.data });
      } catch (error) {
        console.error("Session invalid:", error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setLoading(false);
  };

  const fetchMerchants = async () => {
    try {
      const response = await axios.get(`${API}/public/scanner/merchants`);
      setMerchants(response.data);
    } catch (error) {
      console.error("Error fetching merchants:", error);
    }
  };

  const fetchLocations = async (merchantId) => {
    try {
      const response = await axios.get(`${API}/public/scanner/merchants/${merchantId}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
      setLocations([]);
    }
  };

  const handleMerchantChange = (merchantId) => {
    setSelectedMerchant(merchantId);
    setSelectedLocation("");
    setLocations([]);
    if (merchantId) {
      fetchLocations(merchantId);
    }
  };

  const handleLogin = async () => {
    if (!selectedMerchant || !selectedLocation || !pin) {
      toast.error("Please fill all fields");
      return;
    }

    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/public/scanner/login`, {
        merchant_id: selectedMerchant,
        location_id: selectedLocation,
        pin: pin
      });

      const sessionData = {
        token: response.data.token,
        merchant_name: response.data.merchant_name,
        location_name: response.data.location_name
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
      setSession(sessionData);
      setPin("");
      toast.success(`Logged in as ${response.data.merchant_name} - ${response.data.location_name}`);
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSession(null);
    setScanResult(null);
    stopScanner();
    toast.success("Logged out successfully");
  };

  const startScanner = async () => {
    setScannerError(null);
    setScanResult(null);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } 
      });
      stream.getTracks().forEach(track => track.stop());
      
      const devices = await Html5Qrcode.getCameras();
      
      if (devices && devices.length > 0) {
        const backCamera = devices.find(d => 
          d.label.toLowerCase().includes('back') || 
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        const cameraToUse = backCamera || devices[0];
        
        setIsScanning(true);
        
        setTimeout(() => {
          initializeScanner(cameraToUse.id);
        }, 100);
      } else {
        setScannerError("No cameras found on this device");
        toast.error("No cameras found");
      }
    } catch (err) {
      console.error("Camera access error:", err);
      let errorMessage = "Unable to access camera. ";
      
      if (err.name === "NotAllowedError") {
        errorMessage += "Please allow camera access in your browser settings.";
      } else if (err.name === "NotFoundError") {
        errorMessage += "No camera found on this device.";
      } else {
        errorMessage += err.message || "Please check browser settings.";
      }
      
      setScannerError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const initializeScanner = async (cameraId) => {
    try {
      const html5QrCode = new Html5Qrcode("public-qr-reader");
      html5QrCodeRef.current = html5QrCode;
      
      await html5QrCode.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        onScanSuccess,
        () => {} // Silent error handler
      );
    } catch (err) {
      console.error("Scanner init error:", err);
      setScannerError("Failed to start scanner: " + err.message);
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
      html5QrCodeRef.current = null;
    }
    setIsScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    await stopScanner();
    await verifyQRCode(decodedText);
  };

  const verifyQRCode = async (qrCode) => {
    setVerifying(true);
    try {
      const response = await axios.post(
        `${API}/public/scanner/verify-qr`,
        { qr_code: qrCode },
        { headers: { Authorization: `Bearer ${session.token}` } }
      );

      setScanResult(response.data);

      if (response.data.valid) {
        toast.success("Valid QR code!");
      } else {
        toast.error(response.data.message || "Invalid QR code");
      }
    } catch (error) {
      console.error("Error verifying QR:", error);
      toast.error(error.response?.data?.detail || "Failed to verify QR code");
      setScanResult({
        valid: false,
        message: error.response?.data?.detail || "Verification failed"
      });
    } finally {
      setVerifying(false);
    }
  };

  const markAsUsed = async () => {
    if (!scanResult || !scanResult.usage_id) return;

    try {
      await axios.post(
        `${API}/public/scanner/mark-used/${scanResult.usage_id}`,
        {},
        { headers: { Authorization: `Bearer ${session.token}` } }
      );

      toast.success("Discount applied successfully!");
      setScanResult(null);
    } catch (error) {
      console.error("Error marking as used:", error);
      toast.error(error.response?.data?.detail || "Failed to apply discount");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Login Screen
  if (!session) {
    return (
      <div className="min-h-screen bg-[#0a0f1a]">
        <header className="bg-slate-950 border-b border-white/5">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <Link to="/">
              <Button variant="ghost" className="text-yellow-400 hover:text-yellow-300">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Home
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-yellow-400 uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif" }}>
              SCC Football
            </h1>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-md mx-auto">
            <Card className="p-8 bg-slate-900/50 shadow-lg">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500/20 rounded-full mb-4">
                  <Scan className="w-8 h-8 text-yellow-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Merchant Scanner</h2>
                <p className="text-slate-400">Login to scan member discount QR codes</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    <Store className="w-4 h-4 inline mr-2" />
                    Select Merchant
                  </label>
                  <Select value={selectedMerchant} onValueChange={handleMerchantChange}>
                    <SelectTrigger data-testid="merchant-select">
                      <SelectValue placeholder="Choose merchant..." />
                    </SelectTrigger>
                    <SelectContent>
                      {merchants.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.business_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Select Location
                  </label>
                  <Select 
                    value={selectedLocation} 
                    onValueChange={setSelectedLocation}
                    disabled={!selectedMerchant || locations.length === 0}
                  >
                    <SelectTrigger data-testid="location-select">
                      <SelectValue placeholder={
                        !selectedMerchant ? "Select merchant first" :
                        locations.length === 0 ? "No locations available" :
                        "Choose location..."
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name} {l.address && `- ${l.address}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    <Lock className="w-4 h-4 inline mr-2" />
                    Enter PIN
                  </label>
                  <Input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="Enter 4-6 digit PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-widest"
                    data-testid="pin-input"
                  />
                </div>

                <Button
                  onClick={handleLogin}
                  disabled={loggingIn || !selectedMerchant || !selectedLocation || pin.length < 4}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold py-6 text-lg"
                  data-testid="login-button"
                >
                  {loggingIn ? "Logging in..." : "Login to Scanner"}
                </Button>

                {locations.length === 0 && selectedMerchant && (
                  <p className="text-sm text-amber-400 text-center mt-4">
                    No locations set up for this merchant yet. Please contact the merchant admin.
                  </p>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Scanner Screen (Logged In)
  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold text-yellow-400 uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif" }}>
                SCC Football
              </h1>
              <p className="text-sm text-slate-400">
                {session.merchant_name} - {session.location_name}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-lg mx-auto">
          {/* Scanner Card */}
          <Card className="p-6 bg-slate-900/50 shadow-lg mb-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-500/20 rounded-full mb-3">
                <Camera className="w-7 h-7 text-yellow-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-1">Scan Member QR Code</h2>
              <p className="text-sm text-slate-400">
                Point camera at the member's discount QR code
              </p>
            </div>

            {scannerError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-300">{scannerError}</p>
                </div>
              </div>
            )}

            {!isScanning && !scanResult && (
              <Button
                onClick={startScanner}
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold py-6 text-lg"
                data-testid="start-scan-button"
              >
                <Scan className="w-6 h-6 mr-2" />
                {scannerError ? "Try Again" : "Start Scanner"}
              </Button>
            )}

            {isScanning && (
              <div>
                <div 
                  id="public-qr-reader" 
                  className="mb-4 overflow-hidden rounded-lg bg-slate-800"
                  style={{ minHeight: "280px" }}
                />
                <Button
                  onClick={stopScanner}
                  variant="outline"
                  className="w-full border-slate-600 text-slate-300"
                  data-testid="stop-scan-button"
                >
                  Cancel
                </Button>
              </div>
            )}

            {verifying && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-500 mx-auto mb-3"></div>
                <p className="text-slate-400">Verifying...</p>
              </div>
            )}
          </Card>

          {/* Scan Result */}
          {scanResult && !verifying && (
            <Card className={`p-6 ${scanResult.valid ? 'bg-emerald-500/10 border-2 border-emerald-500/30' : 'bg-red-500/10 border-2 border-red-500/30'}`}>
              <div className="text-center mb-4">
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-3 ${scanResult.valid ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                  {scanResult.valid ? (
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-400" />
                  )}
                </div>
                <h3 className={`text-xl font-bold mb-1 ${scanResult.valid ? 'text-emerald-300' : 'text-red-300'}`}>
                  {scanResult.valid ? "Valid Discount!" : "Invalid"}
                </h3>
                <p className={`text-sm ${scanResult.valid ? 'text-emerald-400' : 'text-red-400'}`}>
                  {scanResult.message || (scanResult.valid ? "Ready to apply" : "Cannot use")}
                </p>
              </div>

              {scanResult.valid && scanResult.discount && (
                <div className="space-y-4">
                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <h4 className="font-bold text-white mb-2">Discount</h4>
                    <p className="text-white">{scanResult.discount.title}</p>
                    <p className="text-yellow-500 font-bold text-lg mt-1">
                      {scanResult.discount.discount_percentage}% OFF
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      Code: {scanResult.discount.discount_code}
                    </p>
                  </div>

                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <h4 className="font-bold text-white mb-2">Member</h4>
                    <p className="text-white">{scanResult.member.name}</p>
                    <p className="text-sm text-slate-400">
                      #{scanResult.member.membership_number}
                    </p>
                    <p className="text-sm text-slate-400 capitalize">
                      {scanResult.member.member_type} member
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={markAsUsed}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-5"
                      data-testid="confirm-discount-button"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Apply Discount
                    </Button>
                    <Button
                      onClick={() => {
                        setScanResult(null);
                        startScanner();
                      }}
                      variant="outline"
                      className="px-4"
                    >
                      Rescan
                    </Button>
                  </div>
                </div>
              )}

              {!scanResult.valid && (
                <Button
                  onClick={() => {
                    setScanResult(null);
                    startScanner();
                  }}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold"
                  data-testid="try-again-button"
                >
                  Scan Another
                </Button>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
